import { useEffect, useMemo, useState } from "react";
import api, { apiErr, API_BASE } from "@/lib/api";
import StudentLayout from "@/components/StudentLayout";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { BookOpen, FileText, FileDown, Clock, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";

function todayISO() {
  const d = new Date();
  const tz = d.getTimezoneOffset();
  return new Date(d.getTime() - tz * 60_000).toISOString().slice(0, 10);
}

const SLATE_TINTS = [
  { board: "#0f172a", chalk: "#fef3c7", nail: "#94a3b8", string: "#475569" },
  { board: "#1e3a8a", chalk: "#dbeafe", nail: "#94a3b8", string: "#1e293b" },
  { board: "#14532d", chalk: "#dcfce7", nail: "#94a3b8", string: "#22543d" },
  { board: "#581c87", chalk: "#f3e8ff", nail: "#94a3b8", string: "#3b0764" },
  { board: "#7c2d12", chalk: "#fee2e2", nail: "#94a3b8", string: "#451a03" },
];

export default function StudentHomework() {
  const [date, setDate] = useState(todayISO());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/student/homework", { params: { date } });
        if (!cancelled) setItems(data.items || []);
      } catch (e) {
        if (!cancelled) toast.error(apiErr(e.response?.data?.detail) || "Could not load homework");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [date]);

  const toggleDone = async (hw) => {
    const next = !hw.done;
    // Optimistic update
    setItems((rows) => rows.map((r) => (r.id === hw.id ? { ...r, done: next } : r)));
    try {
      await api.put(`/student/homework/${hw.id}/done`, { done: next });
      toast.success(next ? "Marked as done 🎉" : "Marked as pending");
    } catch (e) {
      // revert on failure
      setItems((rows) => rows.map((r) => (r.id === hw.id ? { ...r, done: !next } : r)));
      toast.error(apiErr(e.response?.data?.detail) || "Could not update");
    }
  };

  const grouped = useMemo(() => {
    const m = {};
    for (const it of items) (m[it.period] = m[it.period] || []).push(it);
    return Object.entries(m).sort((a, b) => Number(a[0]) - Number(b[0]));
  }, [items]);

  return (
    <StudentLayout>
      <style>{`
        @keyframes swing-l { 0%,100% { transform: rotate(-3.2deg); } 50% { transform: rotate(3.2deg); } }
        @keyframes swing-r { 0%,100% { transform: rotate(2.8deg); } 50% { transform: rotate(-2.8deg); } }
        @keyframes swing-s { 0%,100% { transform: rotate(-2deg); } 50% { transform: rotate(2deg); } }
        .slate-wrap { transform-origin: top center; animation-iteration-count: infinite; animation-timing-function: ease-in-out; }
        .slate-wrap:hover { animation-play-state: paused; }
      `}</style>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Homework wall</div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>Today&apos;s homework</h1>
          <p className="text-slate-500 mt-1">Pick a date to see what was assigned that day.</p>
        </div>
        <DatePicker value={date} onChange={setDate} className="h-11 rounded-xl w-48" data-testid="student-hw-date"/>
      </div>

      {loading ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-10 text-center text-slate-500" data-testid="student-hw-loading">Loading…</div>
      ) : items.length === 0 ? (
        <NoHomeworkCelebration date={date} />
      ) : (
        <div className="space-y-12" data-testid="student-hw-wall">
          {grouped.map(([period, rows], gi) => (
            <section key={period}>
              <div className="flex items-center gap-2 mb-6">
                <Clock className="h-4 w-4 text-sky-500" />
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>Period {period}</div>
                <span className="text-xs text-slate-500">· {rows[0].subject}</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                {rows.map((h, idx) => {
                  const tint = SLATE_TINTS[(gi * 7 + idx) % SLATE_TINTS.length];
                  const swing = idx % 3 === 0 ? "swing-l" : idx % 3 === 1 ? "swing-r" : "swing-s";
                  const dur = 2.4 + ((idx * 0.37) % 1.6);
                  const downloadUrl = `${API_BASE}/student/homework/${h.id}/attachment`;
                  return (
                    <HangingSlate
                      key={h.id}
                      tint={tint}
                      swing={swing}
                      dur={dur}
                      hw={h}
                      downloadUrl={downloadUrl}
                      onToggleDone={() => toggleDone(h)}
                    />
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </StudentLayout>
  );
}

function HangingSlate({ tint, swing, dur, hw, downloadUrl, onToggleDone }) {
  return (
    <div className="relative flex flex-col items-center" data-testid={`student-hw-slate-${hw.id}`}>
      {/* Nail */}
      <div
        className="relative z-10 h-3 w-3 rounded-full shadow"
        style={{ background: `radial-gradient(circle at 30% 30%, #cbd5e1, ${tint.nail} 70%, #475569 100%)` }}
        aria-hidden
      />
      {/* String + slate (animated as one piece, pivot at the nail) */}
      <div
        className="slate-wrap"
        style={{ animationName: swing, animationDuration: `${dur}s` }}
      >
        {/* String */}
        <div className="mx-auto w-px" style={{ height: 28, background: tint.string }} aria-hidden />
        {/* Slate body */}
        <div
          className="relative rounded-md shadow-xl p-5 w-64 sm:w-60 lg:w-64 -mt-px"
          style={{
            background: tint.board,
            border: "8px solid #92400e",
            boxShadow: "0 12px 24px -10px rgba(0,0,0,0.4), inset 0 0 0 2px rgba(255,255,255,0.04)",
            color: tint.chalk,
            fontFamily: "Outfit",
          }}
        >
          {/* Pinhole at top edge of slate (where the string attaches) */}
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-amber-900 border border-amber-950" aria-hidden />
          <div className="flex items-center gap-1.5 mb-2 opacity-80">
            <BookOpen className="h-3.5 w-3.5" />
            <span className="text-[10px] uppercase tracking-[0.2em]">P{hw.period} · {hw.subject}</span>
          </div>
          <h3 className="text-lg font-bold leading-tight mb-2" style={{ fontFamily: "Caveat, Outfit, cursive" }}>
            {hw.title}
          </h3>
          {hw.description && (
            <p
              className="text-sm leading-snug whitespace-pre-wrap mb-3 opacity-95"
              style={{ fontFamily: "Caveat, Outfit, cursive" }}
            >
              {hw.description}
            </p>
          )}
          {hw.has_attachment && (
            <a
              href={downloadUrl}
              target="_blank"
              rel="noreferrer"
              data-testid={`student-hw-download-${hw.id}`}
              className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-white/10 hover:bg-white/20 transition-colors"
              style={{ color: tint.chalk }}
            >
              <FileText className="h-3.5 w-3.5" /> {hw.attachment_name}
              <FileDown className="h-3.5 w-3.5" />
            </a>
          )}
          <div className="mt-3 flex items-center justify-between gap-2">
            <div className="text-[10px] opacity-60">by {hw.created_by_name}</div>
            <button
              type="button"
              onClick={onToggleDone}
              data-testid={`student-hw-done-${hw.id}`}
              className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-1 transition-colors ${hw.done ? "bg-emerald-500/30 hover:bg-emerald-500/40" : "bg-white/10 hover:bg-white/20"}`}
              style={{ color: tint.chalk }}
              aria-label={hw.done ? "Mark as pending" : "Mark as done"}
            >
              {hw.done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
              {hw.done ? "Done" : "Mark done"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function NoHomeworkCelebration({ date }) {
  // Cycle through cheerful greetings on each mount so it doesn't feel canned
  const greetings = [
    { headline: "Heyyy! No homework today 🎉", sub: "Go play, read for fun, or build something cool." },
    { headline: "Hooray — homework-free day!", sub: "Use the time well. Maybe help around the house?" },
    { headline: "Empty slate. Easy day!", sub: "Nothing to do tonight. Sleep early and crush tomorrow." },
    { headline: "Plot twist: no homework!", sub: "Teacher gave you a break. Make it count." },
    { headline: "Free time unlocked 🔓", sub: "Whatever you do today — enjoy it." },
  ];
  const pick = greetings[Math.floor(Math.random() * greetings.length)];

  // Confetti dots — pure CSS, no library
  const dots = Array.from({ length: 28 }).map((_, i) => i);

  return (
    <div className="relative overflow-hidden rounded-3xl border border-emerald-200 dark:border-emerald-800 bg-gradient-to-br from-emerald-50 via-sky-50 to-amber-50 dark:from-emerald-950/40 dark:via-sky-950/30 dark:to-amber-950/30 p-10 sm:p-16 text-center" data-testid="student-hw-empty">
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(-110%) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0.9; }
        }
        @keyframes wiggle {
          0%, 100% { transform: rotate(-6deg); }
          50% { transform: rotate(6deg); }
        }
        @keyframes pop-in {
          0% { transform: scale(0.7); opacity: 0; }
          80% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>

      {/* Confetti rain */}
      <div className="absolute inset-0 pointer-events-none">
        {dots.map((i) => {
          const colors = ["#f43f5e", "#f59e0b", "#22c55e", "#0ea5e9", "#a855f7", "#ec4899", "#14b8a6"];
          const left = (i * 137) % 100;
          const delay = (i % 7) * 0.5;
          const dur = 3 + (i % 5);
          const size = 6 + (i % 4) * 2;
          return (
            <span
              key={i}
              style={{
                position: "absolute",
                left: `${left}%`,
                top: 0,
                width: size,
                height: size * 1.6,
                background: colors[i % colors.length],
                borderRadius: 2,
                animation: `confetti-fall ${dur}s linear ${delay}s infinite`,
              }}
            />
          );
        })}
      </div>

      <div className="relative" style={{ animation: "pop-in 600ms ease-out both" }}>
        <div
          className="mx-auto h-20 w-20 rounded-3xl bg-white dark:bg-slate-800 shadow-lg flex items-center justify-center text-5xl mb-6"
          style={{ animation: "wiggle 1.4s ease-in-out infinite" }}
          aria-hidden
        >
          🎉
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100 mb-3" style={{ fontFamily: "Caveat, Outfit, cursive" }}>
          {pick.headline}
        </h2>
        <p className="text-base text-slate-600 dark:text-slate-300 max-w-md mx-auto mb-5">
          {pick.sub}
        </p>
        <div className="inline-flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 bg-white/70 dark:bg-slate-900/40 backdrop-blur px-3 py-1.5 rounded-full">
          <BookOpen className="h-3.5 w-3.5" />
          Nothing assigned for <b>{date}</b>
        </div>
      </div>
    </div>
  );
}

